import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SegmentedOption<V extends string> {
  value: V;
  label: string;
  icon?: ReactNode;
}

interface Props<V extends string> {
  value: V;
  onValueChange: (value: V) => void;
  options: SegmentedOption<V>[];
  ariaLabel: string;
  size?: 'sm' | 'md';
  /** Compact = icon-only with sr-only labels; tighter padding for phone tier. */
  compact?: boolean;
}

export function SegmentedControl<V extends string>({
  value,
  onValueChange,
  options,
  ariaLabel,
  size = 'sm',
  compact = false,
}: Props<V>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center rounded-full border border-border bg-surface p-0.5 shadow-sm',
        size === 'sm' ? 'h-8' : 'h-9',
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={selected}
            aria-label={opt.label}
            type="button"
            onClick={() => onValueChange(opt.value)}
            className={cn(
              'inline-flex items-center rounded-full font-medium transition-colors',
              size === 'sm' ? 'h-7 text-xs' : 'h-8 text-sm',
              compact ? 'gap-0 px-2' : 'gap-1.5 px-3',
              selected
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-text hover:text-text-primary',
            )}
          >
            {opt.icon}
            <span className={cn(compact && 'sr-only')}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
